'use client'

import { WhiteLabelAssets } from '../../white-label-assets'
import { LoginCard } from '../login-card'
import { LoginCarousel } from '../login-carousel'
import { useThemeStore } from '@/lib/stores/theme-store'

interface LeftLayoutProps {
  children: React.ReactNode
  titleShowcase: string
  brandName: string
  banners?: string | string[]
  /** Quando false, não exibe carrossel de capas (layout “solo” à esquerda). */
  showShowcase?: boolean
}

export function LeftLayout({
  children,
  titleShowcase,
  brandName,
  banners,
  showShowcase = true,
}: LeftLayoutProps) {
  const { currentTheme } = useThemeStore()
  const objectPosition = currentTheme?.login?.layout?.body?.imagePosition || 'center'

  // Check if logoNavOnly is enabled (both new and legacy structures)
  const logoNavOnly =
    currentTheme?.login?.features?.logoNavOnly ||
    (currentTheme as any)?.showcase_content?.login?.logoNavOnly

  // Novas propriedades de título e descrição
  const showTitle = currentTheme?.login?.features?.showTitle ?? true
  const titleText = currentTheme?.login?.features?.titleText || currentTheme?.title
  const showDescription = currentTheme?.login?.features?.showDescription ?? false
  const descriptionText = currentTheme?.login?.features?.descriptionText || currentTheme?.description

  const parsedBanners =
    typeof banners === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(banners)
            return Array.isArray(parsed) ? parsed.filter(Boolean) : (banners ? [banners] : [])
          } catch {
            return banners ? [banners] : []
          }
        })()
      : Array.isArray(banners)
        ? banners.filter(Boolean)
        : []
  const hasBanners = parsedBanners.length > 0
  const showCarousel = showShowcase && hasBanners
  const showSideCopy =
    (showTitle && titleText) || (showDescription && descriptionText)

  return (
    <div className="h-full relative rounded-xl">
      {/* Background with white-label image - fixo */}
      <div className="absolute inset-0 rounded-xl overflow-hidden m-2">
        <WhiteLabelAssets
          type="background"
          className="absolute inset-0 scale-100 origin-right w-full h-full"
          priority={true}
          alt="Login background"
          objectPosition={objectPosition}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/20 to-transparent" />
      </div>

      {/* Container com scroll */}
      <div className="h-full overflow-y-auto">
        <div className="min-h-full flex items-center py-4">
          {/* Wrapper para centralização vertical */}
          <div className="container flex items-center relative z-10">
            {/* Container de conteúdo - altura determinada pelo card */}
            <div className="flex items-end gap-8 w-full">
              {/* Login Card - À esquerda */}
              <div className="flex-shrink-0 flex-grow-0">
                <LoginCard titleShowcase={titleShowcase} brandName={brandName}>
                  {children}
                </LoginCard>
              </div>

              {/* Carrossel - À direita */}
              <div id="carousel-container" className="flex-1 min-w-0 flex flex-col"
                style={{
                  gap: logoNavOnly ? 0 : 32
                }}>
                {/* Title and Description — com vitrine só aparecia com capas; sem vitrine, respeita só as flags */}
                {showSideCopy && (showCarousel || !showShowcase) && (
                  <div className="space-y-2 mb-4">
                    {showTitle && titleText && (
                      <div
                        className="text-4xl font-bold"
                        style={{ color: 'var(--login-body-title)' }}
                        dangerouslySetInnerHTML={{ __html: titleText }}
                      />
                    )}
                    {showDescription && descriptionText && (
                      <div
                        className="text-lg"
                        style={{ color: 'var(--login-body-description)' }}
                        dangerouslySetInnerHTML={{ __html: descriptionText }}
                      />
                    )}
                  </div>
                )}

                {/* Carousel */}
                {showCarousel && <LoginCarousel banners={parsedBanners} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

